import { prepareSectionContentForInsertion, PrepareSectionContentForInsertionOptions } from "./prepareSectionContentForInsertion";
import { parseMarkdownSections } from "./parseMarkdownSections";
import { insertAtSection } from "@/hooks/useWordDocumentUtils/wordInsertion";
import { replaceGroundingMarkersForInsertion, stripLeadingAgentControlJson } from "./groundingMarkers";
import { mutateReportSource, readReportSource, readSectionSnapshot, sectionFingerprint, exportSafeSource, REPORT_RENDER_VERSION } from "./reportSourcePayload";

export interface RenderSectionOptions extends Omit<PrepareSectionContentForInsertionOptions, "parseMarkdownSections"> {
  section: any;
  draftStyles: any;
  grounding?: any;
  groundingTaskId?: string;
  insert?: typeof insertAtSection;
  setGeneratedSections?: any;
  setSectionStatus?: any;
  persistSource?: boolean;
  replaceExisting?: boolean;
  beforeInsert?: () => Promise<void>;
}

/** Rendering only. AI requests, task transitions, review and auth stay with callers. */
export async function renderSectionContent(options: RenderSectionOptions): Promise<void> {
  const { section, grounding, groundingTaskId, markdown } = options;
  const portableSource = options.persistSource !== false && section.fromReportDownload ? await readReportSource() : null;
  if (options.persistSource !== false && section.fromReportDownload &&
      (!portableSource || (options.projectId && portableSource.project_id !== options.projectId) ||
       !portableSource.sections.some((item) => item.settings_group_id === section.id && item.insertion_key === section.insertion_key))) {
    throw new Error("This section does not match the report's embedded source. Reopen the report before regenerating it.");
  }
  const sourceMarkdown = grounding?.markdown || markdown;
  const displayMarkdown = grounding
    ? replaceGroundingMarkersForInsertion(sourceMarkdown, grounding.citation_numbers)
    : section.type?.toLowerCase() === "section_agentic"
      ? replaceGroundingMarkersForInsertion(stripLeadingAgentControlJson(sourceMarkdown)) : sourceMarkdown;
  const items = await prepareSectionContentForInsertion({
    ...options, projectId: options.projectId || portableSource?.project_id, markdown: displayMarkdown, parseMarkdownSections,
  });
  await options.beforeInsert?.();
  await (options.insert || insertAtSection)(
    section.insertion_key, items, 500000,
    options.setGeneratedSections || (() => {}), options.setSectionStatus || (() => {}),
    section, options.draftStyles,
    grounding ? { taskId: grounding.task_id || groundingTaskId, markdown: sourceMarkdown,
      citationNumbers: grounding.citation_numbers } : null,
    { replaceExisting: options.replaceExisting, strict: Boolean(section.fromReportDownload || options.replaceExisting) }
  );
  if (options.persistSource !== false && section.fromReportDownload) {
    const snapshot = await readSectionSnapshot(section.insertion_key);
    const fingerprint = await sectionFingerprint(snapshot.ooxml, section.insertion_key);
    await mutateReportSource((payload) => {
      if (!payload || payload.download_id !== portableSource.download_id) throw new Error("The section was inserted, but this report's embedded source changed.");
      const saved = payload.sections.find((item) => item.settings_group_id === section.id);
      if (!saved) throw new Error("The section was inserted, but its report source could not be found.");
      Object.assign(saved, {
        content: sourceMarkdown, grounding: exportSafeSource(grounding) || null,
        agent_task_id: groundingTaskId || grounding?.task_id || null,
        chat_history_id: section.chat_history_id || null, sources: exportSafeSource(section.sources || []),
        agent_task_status: section.downloadedTaskStatus || saved.agent_task_status || "completed",
        agent_task_updates: Array.isArray(section.agentUpdates) && section.agentUpdates.length > 0
          ? section.agentUpdates
          : (Array.isArray(saved.agent_task_updates) ? saved.agent_task_updates : []),
        image_assets: items.filter((item) => item.sourceAsset).map((item) => item.sourceAsset),
        render_state: { version: REPORT_RENDER_VERSION, complete: true, text: snapshot.text, fingerprint },
      });
      return payload;
    });
  }
}
